import React from 'react'
import { faListUl } from '@fortawesome/pro-regular-svg-icons'
import { FormikProps, useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import { Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { RadioButtonsV2 } from '@labrav/react-components'
import { standardLabraDateFormat } from '@labrav/common'

import { SectionWrapper } from '../../../../common/components/SectionWrapper/SectionWrapper'
import {
  DetailInfo,
  DetailRow,
} from '../../../../common/components/DetailRow/DetailRow'
import { productDetailDate } from '../../../../common/utils/productDetailDate'
import { useTimeZone } from '../../../../common/utils/hooks/useTimeZone'
import { getPricesArray } from '../../../../utils/resaleAuthorizationHelpers'
import { getContractDurationOptions } from '../../../../utils/contractDurationOptions'
import {
  getMappedKey,
  getMappedMonthKey,
} from '../../../../utils/contractDuration'
import type {
  ChannelPartnerPrivateOfferInCreation,
  Dimension,
} from '../../../../types/flyout'
import { ContractDurationType, DiscountType } from '../../../../types/flyout'
import type { RenewChannelOfferResellerAuthorization } from '../renewChannelOfferTypes'
import { useStyles } from './RenewChannelOfferContractDuration.styles'

export const RenewChannelOfferContractDuration = ({
  resellerAuthorization,
}: {
  resellerAuthorization?: RenewChannelOfferResellerAuthorization
}) => {
  const { classes } = useStyles()
  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { values, setFieldValue } = formikProps
  const { timeZone } = useTimeZone()
  const { modifiedDate: previoueContractEndDate } =
    !isEmpty(values) && values?.previousSubscriptionEndDate
      ? productDetailDate(values?.previousSubscriptionEndDate, timeZone)
      : { modifiedDate: '-' }

  const { modifiedDate: agreementStartDate } =
    !isEmpty(values) && values?.agreementStartDate
      ? productDetailDate(values?.agreementStartDate, timeZone)
      : { modifiedDate: '' }

  const prices = getPricesArray(
    (resellerAuthorization?.dimensions || []) as Dimension[]
  )
  const contractDurationOptions = getContractDurationOptions(
    false,
    values.flexiblePaymentTerms as boolean,
    true
  )?.filter(item => prices?.includes(getMappedKey(item?.key)))
  const isMultiPrices = prices.length > 1
  const isMultipleTimeDuration =
    resellerAuthorization?.contractDurationType ===
      ContractDurationType.STANDARD && isMultiPrices

  const checkedValueForSaasContract = () => {
    const selectedKey = values.serviceLength?.toString()
    return selectedKey
  }
  const handleSelectionForContractDuration = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value as string
    const updatedValue = isEmpty(value) ? values?.serviceLength : Number(value)
    await setFieldValue('serviceLength', updatedValue)

    const endDate = resellerAuthorization?.agreementStartDate
      ? DateTime.fromISO(resellerAuthorization?.agreementStartDate, {
          zone: timeZone,
        })
          .plus({ months: updatedValue })
          .toISODate()
      : DateTime.now().plus({ months: updatedValue }).toISODate()
    await setFieldValue('subscriptionEndDate', endDate)
  }

  const singleContractDurationValue =
    resellerAuthorization?.contractDurationType ===
      ContractDurationType.CUSTOM ||
    resellerAuthorization?.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS
      ? resellerAuthorization?.contractDurationInMonthsForEndCustomer
      : getMappedMonthKey(prices[0])

  const contractEndDate = values?.subscriptionEndDate
    ? DateTime.fromISO(values?.subscriptionEndDate).toFormat(
        standardLabraDateFormat
      )
    : null

  return (
    <SectionWrapper
      heading="Contract dates"
      icon={faListUl}
      className={classes.sectionWrapper}
      children={
        <>
          <DetailRow
            borderBottom={false}
            children={
              <>
                <DetailInfo
                  gridSize={6}
                  title={'Previous contract end date'}
                  value={
                    <div className={classes.detailInfo}>
                      {previoueContractEndDate}
                    </div>
                  }
                  keyName="previoueContractEndDate"
                />
                {agreementStartDate && (
                  <DetailInfo
                    gridSize={6}
                    title={'Contract start date'}
                    value={
                      <div className={classes.detailInfo}>
                        {agreementStartDate}
                      </div>
                    }
                    keyName={'agreementStartDate'}
                  />
                )}
              </>
            }
          />
          {isMultipleTimeDuration ? (
            <div
              className={classes.fullWidth}
              data-testid={'duration-selection'}
            >
              <Typography className={classes.sectionHeading}>
                Select contract duration
              </Typography>
              <div className={classes.radioButton}>
                <RadioButtonsV2
                  checkedValue={{
                    key: checkedValueForSaasContract() as string,
                    value: checkedValueForSaasContract() as string,
                  }}
                  name="serviceLength"
                  radioButtonMappings={contractDurationOptions}
                  handleChange={handleSelectionForContractDuration}
                />
              </div>
            </div>
          ) : (
            <>
              <DetailRow
                borderBottom={false}
                children={
                  <DetailInfo
                    gridSize={12}
                    keyName="singleDuration"
                    title={`Authorized contract duration by
                  ${resellerAuthorization?.isvName}`}
                    value={
                      <div className={classes.detailInfo}>
                        {singleContractDurationValue} month
                        {singleContractDurationValue! > 1 ? 's' : ''}
                      </div>
                    }
                  />
                }
              />
            </>
          )}
          <DetailRow
            borderBottom={false}
            children={
              <DetailInfo
                gridSize={12}
                keyName="contractEndDate"
                title={'Contract end date'}
                value={
                  <div className={classes.detailInfo}>{contractEndDate}</div>
                }
              />
            }
          />
        </>
      }
    />
  )
}
